import { Pipe, PipeTransform, inject } from '@angular/core';
import { VTranslateService } from '../../services/v-translate.service';

/* 
 * Usage:
 * token | vtranslate
 * token | vtranslate:data
 * 
 * Example:
 * {{ 'hello.world' | vtranslate }}
 * {{ 'user.greeting' | vtranslate:{ name: 'John' } }}
 */
@Pipe({
    name:       'vtranslate',
    standalone: true,
    pure:       false
})
export class VTranslatePipe implements PipeTransform {
    private translateService = inject(VTranslateService);
    private cache = new Map<string, { signal: ReturnType<VTranslateService['asSignal']>, lastData: unknown }>();

    transform<T>(token: string, data?: T): string {
        const cacheKey = token;
        const cached = this.cache.get(cacheKey);

        // Check if we have a cached signal and if the data hasn't changed
        if (cached && this.dataEquals(cached.lastData, data)) {
            const translation = cached.signal();
            return typeof translation === 'string' ? translation : token; // Fallback to token if translation is not a string
        }

        // Create new signal and cache it
        const signal = this.translateService.asSignal(token, data);
        this.cache.set(cacheKey, { signal, lastData: data });

        const translation = signal();
        return typeof translation === 'string' ? translation : token; // Fallback to token if translation is not a string
    }

    private dataEquals(a: unknown, b: unknown): boolean {
        if (a === b) return true;
        if (!a && !b) return true;
        if (!a || !b) return false;

        // Simple deep comparison for objects
        if (typeof a === 'object' && typeof b === 'object') {
            const keysA = Object.keys(a as Record<string, unknown>);
            const keysB = Object.keys(b as Record<string, unknown>);

            if (keysA.length !== keysB.length) return false;
            for (const key of keysA)
                if ((a as Record<string, unknown>)[key] !== (b as Record<string, unknown>)[key]) return false;

            return true;
        }
        return false;
    }
}
